import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import { Tab, Tabs, Divider, Alert } from "@heroui/react";
import {
    createFileRoute,
    Link,
    Outlet,
    useMatch,
    useParams,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { ImageryType } from "@/types/imagery";
import useFeatureFlag from "@/lib/featureFlags/useFeatureFlags";

export const Route = createFileRoute("/dashboard/fields/$fieldId/insights")({
    component: RouteComponent,
    validateSearch: (search: {
        advisor: boolean | undefined;
    }): { advisor: boolean | undefined } => {
        return {
            advisor: search.advisor,
        };
    },
});

function RouteComponent() {
    const { fieldId } = useParams({
        from: "/dashboard/fields/$fieldId/insights",
    });

    const { setImageType, fieldIsTrial } = useFieldDashboard() ?? {};
    const { samplesEnabled, irrigationEnabled } = useFeatureFlag();

    const ntime = useMatch({
        from: "/dashboard/fields/$fieldId/insights/ntime",
        shouldThrow: false,
    });
    const hydroflow = useMatch({
        from: "/dashboard/fields/$fieldId/insights/hydroflow",
        shouldThrow: false,
    });
    const development = useMatch({
        from: "/dashboard/fields/$fieldId/insights/development",
        shouldThrow: false,
    });
    const weather = useMatch({
        from: "/dashboard/fields/$fieldId/insights/weather",
        shouldThrow: false,
    });
    const samples = useMatch({
        from: "/dashboard/fields/$fieldId/insights/samples",
        shouldThrow: false,
    });
    const tab =
        [ntime, hydroflow, development, weather, samples]
            ?.find((x) => !!x)
            ?.fullPath?.split("/")
            .pop() ?? "ntime";

    useEffect(() => {
        if (tab === "ntime") {
            setImageType?.(ImageryType.NDRE);
        }
        if (tab === "hydroflow") {
            setImageType?.(ImageryType.SOIL_WATER_CONTENT);
            return () => {
                setImageType?.(ImageryType.NDRE);
            };
        }
        if (tab === "development") {
            setImageType?.(ImageryType.NDVI);
        }
        if (tab === "weather") {
            setImageType?.(ImageryType.RGB);
        }
        if (tab === "samples") {
            setImageType?.(ImageryType.NDRE);
        }
    }, [tab, setImageType]);

    return (
        <div className="flex flex-col ">
            {fieldIsTrial ? (
                <div className="p-2 pt-0">
                    <Alert
                        color="warning"
                        variant="flat"
                        title="Trial Mode"
                        description="You will only see N-Time imagery for this field"
                    />
                </div>
            ) : (
                <>
                    <Tabs
                        fullWidth
                        color="secondary"
                        variant="underlined"
                        radius="sm"
                        classNames={{
                            tabList: "p-0 pt-1",
                        }}
                        selectedKey={tab}
                    >
                        <Tab
                            key="ntime"
                            title="N-Time"
                            as={Link}
                            // @ts-expect-error ts(2322 )
                            to="/dashboard/fields/$fieldId/insights/ntime"
                            params={{ fieldId }}
                            search={(prev: Record<string, unknown>) => prev}
                        />
                        {irrigationEnabled && (
                            <Tab
                                key="hydroflow"
                                title="HydroFlow"
                                as={Link}
                                // @ts-expect-error ts(2322 )
                                to="/dashboard/fields/$fieldId/insights/hydroflow"
                                params={{ fieldId }}
                                search={(prev: Record<string, unknown>) => prev}
                            />
                        )}
                        <Tab
                            key="development"
                            title="Development"
                            as={Link}
                            // @ts-expect-error ts(2322 )
                            to="/dashboard/fields/$fieldId/insights/development"
                            params={{ fieldId }}
                            search={(prev: Record<string, unknown>) => prev}
                        />
                        <Tab
                            key="weather"
                            title="Weather"
                            as={Link}
                            // @ts-expect-error ts(2322 )
                            to="/dashboard/fields/$fieldId/insights/weather"
                            params={{ fieldId }}
                            search={(prev: Record<string, unknown>) => prev}
                        />
                        {samplesEnabled && (
                            <Tab
                                key="samples"
                                title="Samples"
                                as={Link}
                                // @ts-expect-error ts(2322 )
                                to="/dashboard/fields/$fieldId/insights/samples"
                                params={{ fieldId }}
                                search={(prev: Record<string, unknown>) => prev}
                            />
                        )}
                    </Tabs>
                    <Divider />
                    <div className="p-2 pt-0">
                        <Outlet />
                    </div>
                </>
            )}
        </div>
    );
}
